import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Typography,
  useTheme
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { isMobile } from 'react-device-detect';
import api from '../../api';
import { formatDateTime, sortByDay } from '../../utils/general';
import DeleteButton from '../../components/list/DeleteButton';
import DeleteModal from '../../components/modals/DeleteModal';
import { dayFullDateParser } from '../../utils/widgetHelper';
import {
  mainColors,
  secondaryColors,
  themeFont,
  themePalette
} from '../../theme/themeConfig';
import useNotificationsStore from '../../store/zustand/notifications';

type BearerContents = { companyId: string; roles: string[]; userId: string };

// ── Avatar with initials ──────────────────────────────────────────────────
function NameAvatar({ name, color }: { color: string; name: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: color,
        borderRadius: '50%',
        color: '#fff',
        display: 'flex',
        flexShrink: 0,
        fontSize: '12px',
        fontWeight: 700,
        height: 34,
        justifyContent: 'center',
        width: 34
      }}
    >
      {initials}
    </Box>
  );
}

// Color from name hash
function nameToColor(name: string) {
  const palette = [
    '#189A90',
    '#005DA2',
    '#E65100',
    '#6A1B9A',
    '#1565C0',
    '#2E7D32',
    '#4527A0'
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1)
    hash = name.charCodeAt(i) + (hash * 31 - hash);
  return palette[Math.abs(hash) % palette.length];
}

interface DataNote {
  createdAt: string;
  id: string;
  noteText: string;
  operator: {
    fileBank: { fileUrl: string }[];
    firstName: string;
    lastName: string;
    userRoles: { role: { name: string } }[];
  };
}
interface DataEntity {
  createdAt: string;
  id: string;
  noteText: string;
  user: {
    fileBank: { fileUrl: string }[];
    firstName: string;
    lastName: string;
    userRoles: { role: { name: string } }[];
  };
}

function parseEntityData(data: DataEntity[]) {
  return data.map((item) => ({
    createdAt: item.createdAt,
    fileUrl: item.user.fileBank[0]?.fileUrl,
    id: item.id,
    name: `${item.user.firstName} ${item.user.lastName}`,
    noteText: item.noteText,
    sendByType:
      item.user.userRoles.length > 0 ? item.user.userRoles[0].role.name : null
  }));
}

function parseNoteData(data: DataNote[]) {
  return data.map((note) => ({
    createdAt: note.createdAt,
    fileUrl: note.operator?.fileBank?.[0]?.fileUrl ?? '',
    id: note.id,
    name: `${note.operator.firstName} ${note.operator.lastName}`,
    noteText: note.noteText,
    sendByType: note.operator?.userRoles?.[0].role.name
  }));
}

interface Props {
  entity?: string;
  eventId?: string;
  hideDeleteButton?: boolean;
  id?: string;
  technicianNotes?: boolean;
  technicianView?: boolean;
}

const Notes: React.FC<Props> = function Notes({
  eventId,
  hideDeleteButton,
  id,
  entity,
  technicianView,
  technicianNotes
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { addNotification } = useNotificationsStore((state) => state);
  const deleteAction = api.ProjectApi.deleteNotes;
  const [postMessageApi, setPostMessageApi] = useState<boolean>(false);
  const [notesData, setNotesData] = useState([]);
  const [message, setMessage] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [openContactInfo, setOpenContactInfo] = useState<boolean>(false);
  const [deleteItem, setDelete] = useState<string | null>(null);
  const messageRef = useRef<any>();
  const [contactInformation, setContactInformation] = useState<any>();
  const [
    fireFindContactInformationByCalendarEvent,
    setFireFindContactInformationByCalendarEvent
  ] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const bearerToken = useMemo(
    () => `Bearer ${localStorage.getItem('accessToken')?.replaceAll('"', '')}`,
    []
  );
  const { roles: tokenRoles }: BearerContents = jwtDecode(bearerToken);

  const getAction = entity
    ? api.NotesApi.getAllByEntity
    : api.ProjectApi.getNotes;
  const getObject = entity
    ? { entity, entityId: id, url: `notes/${entity}/${id}` }
    : { id, url: `projects/notes/${id}` };
  const { data: notesResponse, mutate: mutateNotes } = useSWR(
    id ? getObject : null,
    getAction
  );

  const postAction = entity ? api.NotesApi.postNote : api.ProjectApi.postNote;
  const postObject = entity
    ? {
        entity,
        entityId: `${id}`,
        noteText: message,
        taskIterationId: id ? parseInt(id, 10) : 0
      }
    : { id, text: message, title: '', url: 'postNote' };
  const {
    data: postMessageData,
    mutate: postMessageMutate,
    error: postMessageError
  } = useSWR(postMessageApi ? postObject : null, postAction);

  const { data: contactInformationResponse } = useSWR(
    fireFindContactInformationByCalendarEvent && eventId
      ? { id: eventId }
      : null,
    api.CalendarApi.findContactInformationByCalendarEvent
  );

  const clearApiResponse = () => {
    postMessageMutate(undefined, true);
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        behavior: 'smooth',
        top: containerRef.current.scrollHeight
      });
    }
  }, [notesData]);

  useEffect(() => {
    if (notesResponse) setNotesData(notesResponse);
  }, [notesResponse]);

  useEffect(() => {
    if (postMessageData) {
      setMessage('');
      setPostMessageApi(false);
      mutateNotes();
      addNotification(t('NOTIFICATION.TOAST.messageSent'), 'success');
    }
  }, [postMessageData]);

  useEffect(() => {
    if (postMessageError) {
      setPostMessageApi(false);
      addNotification(t('NOTIFICATION.TOAST.messageFail'), 'error');
    }
  }, [postMessageError]);

  useEffect(() => {
    setFireFindContactInformationByCalendarEvent(true);
  }, [id]);

  useEffect(() => {
    if (contactInformationResponse) {
      setFireFindContactInformationByCalendarEvent(false);
      setContactInformation(contactInformationResponse);
    }
  }, [contactInformationResponse]);

  // ── Technician views (unchanged legacy) ────────────────────────────────
  const getTechnicianMessages = () => {
    const getMessagesParsed = sortByDay(notesData, 'createdAt');
    const keys = Object.keys(getMessagesParsed);
    const taskList = keys.map((date: string) => {
      return getMessagesParsed?.[date].map((item: any) => (
        <Box
          key={date}
          sx={{
            alignItems: 'center',
            backgroundColor: themePalette.BACKGROUND,
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mb: '8px',
            minHeight: '52px',
            padding: '16px'
          }}
        >
          <Typography
            sx={{
              color: '#4B4D56',
              fontFamily: themeFont.FONT_SECONDARY,
              fontSize: '14px'
            }}
          >
            {item?.noteText}
          </Typography>
        </Box>
      ));
    });
    return (
      <Box
        sx={{
          border: `1px solid ${secondaryColors.SECONDARY_2}`,
          borderRadius: '8px',
          height: '236px',
          overflowY: 'scroll',
          padding: '16px 16px 8px 16px'
        }}
      >
        {taskList}
      </Box>
    );
  };

  const getContactInfo = () => (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.primary[100]}`,
        borderRadius: '8px',
        padding: '16px'
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%'
        }}
      >
        <Typography
          fontFamily={themeFont.FONT_SECONDARY}
          fontSize={14}
          fontWeight={400}
          lineHeight="20px"
        >
          Support
        </Typography>
        <Button
          onClick={() => setOpenContactInfo(!openContactInfo)}
          sx={{ minWidth: 'auto', padding: '0px' }}
        >
          <i
            className="custom-icon icon-select"
            style={{
              color: secondaryColors.SECONDARY,
              fontSize: 9,
              padding: '0px',
              transform: `rotate(${openContactInfo ? '180deg' : '0deg'})`
            }}
          />
        </Button>
      </Box>
      <Collapse in={openContactInfo}>
        <Box display="flex" flexDirection="column" gap={1} mt={1.5}>
          {contactInformation?.supervisorPhoneNumber && (
            <Box
              sx={{
                alignItems: 'center',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                minHeight: '40px'
              }}
            >
              <Typography
                sx={{
                  color: '#4B4D56',
                  fontFamily: themeFont.FONT_SECONDARY,
                  fontSize: '14px'
                }}
              >
                {t('MODAL.supervisorNumber', [
                  contactInformation.supervisorPhoneNumber
                ])}
              </Typography>
              <CallOutlinedIcon sx={{ fill: mainColors.MAIN }} />
            </Box>
          )}
          {contactInformation?.officePhoneNumber && (
            <Box
              sx={{
                alignItems: 'center',
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                minHeight: '40px'
              }}
            >
              <Typography
                sx={{
                  color: '#4B4D56',
                  fontFamily: themeFont.FONT_SECONDARY,
                  fontSize: '14px'
                }}
              >
                {t('MODAL.officeNumber', [
                  contactInformation.officePhoneNumber
                ])}
              </Typography>
              <CallOutlinedIcon sx={{ fill: mainColors.MAIN }} />
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );

  // ── Standard notes view (redesigned) ───────────────────────────────────
  const getMessages = () => {
    const parsed = entity
      ? parseEntityData(notesData)
      : parseNoteData(notesData);
    const sorted = sortByDay(parsed, 'createdAt');
    return Object.keys(sorted).map((date) => (
      <Box key={date} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Date divider */}
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, my: '2px' }}>
          <Box sx={{ backgroundColor: '#E2E4EA', flex: 1, height: '1px' }} />
          <Typography
            sx={{ color: '#CBCCD3', fontSize: '11px', fontWeight: 500, px: 1 }}
          >
            {dayFullDateParser(date)}
          </Typography>
          <Box sx={{ backgroundColor: '#E2E4EA', flex: 1, height: '1px' }} />
        </Box>
        {sorted[date].map((item: any) => {
          const color = item.fileUrl ? undefined : nameToColor(item.name);
          return (
            <Box
              key={item.id}
              sx={{
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
                backgroundColor: '#fff',
                border: '1px solid #E2E4EA',
                borderRadius: '10px',
                p: '14px 16px',
                transition: 'box-shadow 0.15s'
              }}
            >
              {/* Header row */}
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: '10px',
                  mb: '10px'
                }}
              >
                {item.fileUrl ? (
                  <Avatar
                    alt={item.name}
                    src={item.fileUrl}
                    sx={{ height: 34, width: 34 }}
                  />
                ) : (
                  <NameAvatar color={color!} name={item.name} />
                )}
                <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                  <Typography
                    sx={{ color: '#0f1c2e', fontSize: '13px', fontWeight: 600 }}
                  >
                    {item.name}
                  </Typography>
                </Box>
                <Typography
                  sx={{ color: '#CBCCD3', flexShrink: 0, fontSize: '11px' }}
                >
                  {formatDateTime(item?.createdAt)}
                </Typography>
                {!hideDeleteButton && !technicianView && (
                  <DeleteButton
                    deleteFn={() => {
                      setDelete(item.id);
                      setShowDeleteModal(true);
                    }}
                  />
                )}
              </Box>
              {/* Note text */}
              <Typography
                sx={{
                  color: '#4B4D56',
                  fontSize: '13px',
                  lineHeight: 1.6,
                  ml: '44px'
                }}
              >
                {item.noteText}
              </Typography>
            </Box>
          );
        })}
      </Box>
    ));
  };

  // ── Input box (new style) ───────────────────────────────────────────────
  const inputBox = () => (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: '1px solid #E2E4EA',
        borderRadius: '10px',
        mb: 3,
        overflow: 'hidden'
      }}
    >
      <textarea
        disabled={postMessageApi}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            if (message?.trim()) {
              clearApiResponse();
              setPostMessageApi(true);
            }
          }
        }}
        placeholder="Add a note about this project…"
        style={{
          border: 'none',
          color: '#4B4D56',
          fontFamily: 'inherit',
          fontSize: '13px',
          height: 90,
          outline: 'none',
          padding: '14px 16px',
          resize: 'none',
          width: '100%'
        }}
        value={message}
      />
      <Box
        sx={{
          borderTop: '1px solid #F0F1F3',
          display: 'flex',
          justifyContent: 'flex-end',
          px: 2,
          py: '10px'
        }}
      >
        <Button
          disabled={postMessageApi || !message?.trim()}
          onClick={() => {
            if (!message?.trim()) {
              addNotification(t('NOTIFICATION.TOAST.messageEmpty'), 'error');
              return;
            }
            clearApiResponse();
            setPostMessageApi(true);
          }}
          sx={{
            '&:hover': { backgroundColor: '#004d8a' },
            '&:disabled': { backgroundColor: '#CBCCD3', color: '#fff' },
            backgroundColor: '#005DA2',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 600,
            gap: '6px',
            px: '16px',
            py: '8px',
            textTransform: 'none'
          }}
          variant="contained"
        >
          <SendIcon sx={{ fontSize: 13 }} />
          Post Note
        </Button>
      </Box>
    </Box>
  );

  // ── Root render ─────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%'
      }}
    >
      {!technicianView && !technicianNotes && (
        <>
          <Typography
            sx={{ color: '#0f1c2e', fontSize: '18px', fontWeight: 700, mb: 3 }}
          >
            Notes
          </Typography>
          {inputBox()}
        </>
      )}

      {/* Messages area */}
      <Box
        className="scrollbar"
        ref={containerRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxHeight: technicianView ? '300px' : '600px',
          overflowY: 'auto',
          p: technicianView ? 2 : 0
        }}
      >
        {!technicianView && notesData?.length > 0 && getMessages()}
        {technicianView && notesData?.length > 0 && getTechnicianMessages()}
        {technicianView &&
          contactInformation?.officePhoneNumber &&
          getContactInfo()}
        <Box ref={messageRef} />
      </Box>

      {/* Technician input box */}
      {technicianView && (
        <Box
          sx={{
            alignItems: 'center',
            borderTop: `1px solid ${secondaryColors.SECONDARY_6}`,
            display: 'flex',
            gap: 1.5,
            mt: 1,
            pt: 1.5
          }}
        >
          <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
            {postMessageApi ? (
              <input
                disabled
                style={{
                  border: '1px solid #E2E4EA',
                  borderRadius: '6px',
                  height: '40px',
                  width: '100%'
                }}
              />
            ) : (
              <input
                onChange={(e) => setMessage(e.target.value)}
                style={{
                  border: '1px solid #E2E4EA',
                  borderRadius: '6px',
                  height: '40px',
                  padding: '0 12px',
                  width: '100%'
                }}
                value={message}
              />
            )}
          </Box>
          <Button
            onClick={() => {
              if (!message || message === '') {
                addNotification(t('NOTIFICATION.TOAST.messageEmpty'), 'error');
                return;
              }
              clearApiResponse();
              setPostMessageApi(true);
            }}
            sx={{
              borderRadius: '6px',
              flexShrink: 0,
              height: 40,
              minWidth: isMobile ? 40 : 120,
              px: isMobile ? 1 : 2
            }}
            variant="primary"
          >
            {isMobile ? (
              <SendIcon fontSize="small" />
            ) : (
              <Typography
                sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}
                variant="primary-bold"
              >
                <SendIcon fontSize="small" />
                {t('LEAD.CREATE.PROPOSAL.CHAT.button')}
              </Typography>
            )}
          </Button>
        </Box>
      )}

      {showDeleteModal && (
        <DeleteModal
          action={deleteAction}
          id={{ id: deleteItem, url: `/delete/projects-notes-${deleteItem}` }}
          mutate={() => {
            setShowDeleteModal(false);
            mutateNotes();
          }}
          setShowModal={setShowDeleteModal}
        />
      )}
    </Box>
  );
};

Notes.defaultProps = {
  entity: '',
  eventId: '',
  hideDeleteButton: false,
  id: '',
  technicianNotes: false,
  technicianView: false
};

export default Notes;
